import React, {ReactNode, useEffect, useMemo, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	LinearProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import {alpha, keyframes} from "@mui/material/styles";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import {
	EmpTimeClockRestControllerApiFactory,
	TimeClockCorrectionDto,
	TimeClockCorrectionRequest,
	TimeClockEntryDto,
	TimeClockPersonMonth,
	TimeClockStatusDto,
} from "arteco-api-client-ts";
import dayjs, {Dayjs} from "dayjs";
import AdminInner from "../../../components/layout/admin_inner";
import {useApiManager} from "../../../components/utils/api";
import {COLOR_AZUL_OSCURO, COLOR_ROJO_CLARO, COLOR_VERDE_ARTECO} from "../../../components/layout/colors";
import {PushToggle} from "./push_toggle";
import {PushAsk} from "./push_ask";
import {InstallCard} from "./install_card";
import {duration, hhmm, hours, longDay, panelSx, shortDay, STATUS_COLOR, STATUS_LABEL} from "./timeclock_util";

const pulse = keyframes`
	0% { box-shadow: 0 0 0 0 ${alpha(COLOR_VERDE_ARTECO, 0.7)}; }
	70% { box-shadow: 0 0 0 8px ${alpha(COLOR_VERDE_ARTECO, 0)}; }
	100% { box-shadow: 0 0 0 0 ${alpha(COLOR_VERDE_ARTECO, 0)}; }
`;

/** Minutos desde medianoche de un LocalDateTime del API. */
const minuteOfDay = (dt?: string | null): number => dt ? dayjs(dt).hour() * 60 + dayjs(dt).minute() : 0;

/** La hora del servidor, que avanza con el reloj del navegador: la que se guardará al pulsar. */
const useServerClock = (serverTime?: string): Dayjs => {
	const [offset, setOffset] = useState(0);
	const [now, setNow] = useState(dayjs());
	useEffect(() => {
		if (serverTime) {
			setOffset(dayjs(serverTime).diff(dayjs()));
		}
	}, [serverTime]);
	useEffect(() => {
		const t = setInterval(() => setNow(dayjs()), 1000);
		return () => clearInterval(t);
	}, []);
	return now.add(offset, "ms");
};

/**
 * Fichar desde el portal (#13709): la tarjeta principal con la hora, el estado y el botón de entrada o
 * salida; los tramos de hoy en una línea de tiempo, el mes, los días por corregir y los avisos. La hora
 * la pone el servidor al fichar; aquí solo se proponen correcciones, que valida RR. HH.
 */
export const EmployeeTimeClockPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(EmpTimeClockRestControllerApiFactory);
	const [status, setStatus] = useState<TimeClockStatusDto>();
	const [month, setMonth] = useState<TimeClockPersonMonth>();
	const [busy, setBusy] = useState(false);
	const [correcting, setCorrecting] = useState<TimeClockEntryDto | "new" | undefined>();
	const now = useServerClock(status?.serverTime);

	const loadMonth = () => {
		const today = dayjs();
		apiManager.execute(ctrl.myMonth(today.year(), today.month() + 1)).then(setMonth).catch(() => undefined);
	};

	useEffect(() => {
		apiManager.execute(ctrl.statusTimeClock()).then(setStatus).catch(() => undefined);
		loadMonth();
	}, []);

	const clock = () => {
		if (!status) {
			return;
		}
		setBusy(true);
		const call = status.open ? ctrl.clockOut() : ctrl.clockIn();
		apiManager.execute(call, status.open ? "Salida registrada" : "Entrada registrada")
			.then(s => {
				setStatus(s);
				loadMonth();
			})
			.catch(() => undefined)
			.finally(() => setBusy(false));
	};

	const afterCorrection = () => {
		setCorrecting(undefined);
		apiManager.execute(ctrl.statusTimeClock()).then(setStatus).catch(() => undefined);
		loadMonth();
	};

	if (!status) {
		return <AdminInner withoutPaper>
			<Box display="flex" justifyContent="center" p={6}><CircularProgress/></Box>
		</AdminInner>;
	}

	const entries = status.todayEntries || [];
	const incomplete = status.incomplete || [];
	const corrections = status.corrections || [];
	const openEntry = entries.find(e => e.status === "OPEN");
	// El tramo abierto cuenta hasta ahora, para que el total avance mientras se mira la pantalla.
	const workedToday = entries.reduce((acc, e) => acc + (e.status === "OPEN"
		? Math.max(0, now.diff(dayjs(e.startAt), "minute"))
		: (e.workedMinutes || 0)), 0);

	return (
		<AdminInner withoutPaper>
			<Box sx={{maxWidth: 1120}}>
				<Box sx={{mb: 3}}>
					<Typography variant="h5" component="h1" fontWeight={700}>Registro de jornada</Typography>
					<Typography variant="body2" color="text.secondary">
						Ficha la entrada y la salida de cada tramo. Si se te olvida, propón la hora real y RR. HH. la valida.
					</Typography>
				</Box>

				<InstallCard sx={{mb: 3}}/>
				<PushAsk/>

				<Grid container spacing={3}>
					<Grid item xs={12} md={7}>
						<ClockCard status={status} now={now} openEntry={openEntry} workedToday={workedToday}
								   busy={busy} onClock={clock}/>
					</Grid>
					<Grid item xs={12} md={5}>
						<Stack spacing={3} height="100%">
							<TodayCard entries={entries} workedToday={workedToday} now={now}
									   startTime={status.startTime} endTime={status.endTime}/>
							{month && <MonthCard month={month} onPropose={() => setCorrecting("new")}/>}
						</Stack>
					</Grid>

					<Grid item xs={12} md={7}>
						<CorrectionsCard incomplete={incomplete} corrections={corrections}
										 onCorrect={setCorrecting} onPropose={() => setCorrecting("new")}/>
					</Grid>
					<Grid item xs={12} md={5}>
						<PushToggle/>
					</Grid>
				</Grid>

				<Stack direction="row" spacing={1} alignItems="flex-start" sx={{mt: 3, color: "text.secondary"}}>
					<InfoOutlinedIcon sx={{fontSize: 18, mt: "2px"}}/>
					<Typography variant="caption">
						La hora la registra el servidor en el momento de pulsar. Un día con la salida sin fichar
						queda incompleto y no se rellena solo. El registro se conserva cuatro años y no se borra.
					</Typography>
				</Stack>
			</Box>

			{correcting && (
				<CorrectionDialog
					entry={correcting === "new" ? undefined : correcting}
					onClose={() => setCorrecting(undefined)}
					onSaved={afterCorrection}
				/>
			)}
		</AdminInner>
	);
};

interface ClockCardProps {
	status: TimeClockStatusDto;
	now: Dayjs;
	openEntry?: TimeClockEntryDto;
	workedToday: number;
	busy: boolean;
	onClock: () => void;
}

/** La hora en grande, el estado de la jornada, el avance del día y el botón. */
const ClockCard = ({status, now, openEntry, workedToday, busy, onClock}: ClockCardProps) => {
	const open = !!status.open;
	const expectedMinutes = Math.round((status.expectedHours || 0) * 60);
	const progress = expectedMinutes > 0 ? Math.min(100, workedToday * 100 / expectedMinutes) : 0;
	const subtitle = open
		? `Entrada a las ${hhmm(openEntry?.startAt)} · llevas ${duration(workedToday)}`
		: workedToday > 0
			? `Hoy llevas ${duration(workedToday)} fichadas`
			: "Hoy aún no has fichado";

	return (
		<Card elevation={0} sx={{
			height: "100%",
			borderRadius: 3,
			color: "#fff",
			p: {xs: 3, sm: 4},
			background: `linear-gradient(135deg, ${COLOR_AZUL_OSCURO} 0%, #2f4a6e 100%)`,
			display: "flex",
			flexDirection: "column",
		}}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
				<Typography variant="body2" sx={{opacity: 0.75, fontWeight: 600}}>{longDay(status.today)}</Typography>
				<Stack direction="row" alignItems="center" spacing={1} sx={{
					px: 1.5, py: 0.5, borderRadius: 99, bgcolor: alpha("#fff", 0.1), whiteSpace: "nowrap",
				}}>
					<Box sx={{
						width: 8, height: 8, borderRadius: "50%",
						bgcolor: open ? COLOR_VERDE_ARTECO : alpha("#fff", 0.5),
						animation: open ? `${pulse} 2s infinite` : "none",
					}}/>
					<Typography variant="caption" fontWeight={600}>{open ? "En jornada" : "Fuera de jornada"}</Typography>
				</Stack>
			</Stack>

			<Box sx={{my: {xs: 3, sm: 4}}}>
				<Typography component="p" sx={{
					fontSize: {xs: "3.75rem", sm: "4.5rem"}, lineHeight: 1, fontWeight: 300,
					fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em",
				}}>
					{now.format("HH:mm")}
					<Box component="span" sx={{fontSize: "0.4em", opacity: 0.5, ml: 0.5}}>{now.format("ss")}</Box>
				</Typography>
				<Typography variant="body1" sx={{mt: 1.5, opacity: 0.85}}>{subtitle}</Typography>
			</Box>

			{expectedMinutes > 0 && (
				<Box sx={{mb: 3}}>
					<Stack direction="row" justifyContent="space-between" sx={{mb: 0.75}}>
						<Typography variant="caption" sx={{opacity: 0.75}}>Jornada de hoy</Typography>
						<Typography variant="caption" sx={{opacity: 0.75, fontVariantNumeric: "tabular-nums"}}>
							{duration(workedToday)} de {duration(expectedMinutes)}
						</Typography>
					</Stack>
					<LinearProgress variant="determinate" value={progress} sx={{
						height: 6, borderRadius: 3, bgcolor: alpha("#fff", 0.15),
						"& .MuiLinearProgress-bar": {borderRadius: 3, bgcolor: COLOR_VERDE_ARTECO},
					}}/>
				</Box>
			)}

			{!status.workingDay && (
				<Alert severity="info" variant="filled" icon={<EventAvailableRoundedIcon fontSize="inherit"/>} sx={{
					mb: 2, bgcolor: alpha("#fff", 0.12), color: "#fff", borderRadius: 2,
				}}>
					Hoy no es laborable para ti{status.absence ? ` (${status.absence})` : ""}. Si trabajas, ficha igualmente.
				</Alert>
			)}

			<Box sx={{mt: "auto"}}>
				<Button
					fullWidth
					variant="contained"
					disableElevation
					size="large"
					startIcon={open ? <LogoutRoundedIcon/> : <LoginRoundedIcon/>}
					disabled={busy}
					onClick={onClock}
					data-testid="timeclock-main-button"
					sx={{
						py: 2, borderRadius: 2.5, fontSize: "1.1rem", fontWeight: 700, textTransform: "none",
						bgcolor: open ? COLOR_ROJO_CLARO : COLOR_VERDE_ARTECO,
						color: open ? "#fff" : COLOR_AZUL_OSCURO,
						"& .MuiButton-startIcon > *:nth-of-type(1)": {fontSize: 26},
						"&:hover": {bgcolor: open ? "#e2504e" : "#22c9a8"},
						"&.Mui-disabled": {bgcolor: alpha("#fff", 0.2), color: alpha("#fff", 0.6)},
					}}
				>
					{busy ? <CircularProgress size={26} color="inherit"/> : open ? "Fichar salida" : "Fichar entrada"}
				</Button>
				<Typography variant="caption" display="block" textAlign="center" sx={{mt: 1.5, opacity: 0.6}}>
					Horario de referencia para los avisos: {status.startTime} – {status.endTime}
				</Typography>
			</Box>
		</Card>
	);
};

const SectionTitle = ({children, action}: { children: ReactNode, action?: ReactNode }) => (
	<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{mb: 2}}>
		<Typography variant="subtitle1" fontWeight={700}>{children}</Typography>
		{action}
	</Stack>
);

interface TodayCardProps {
	entries: TimeClockEntryDto[];
	workedToday: number;
	now: Dayjs;
	startTime?: string;
	endTime?: string;
}

/** Los tramos de hoy sobre una línea de 6:00 a 22:00 (o más, si alguno se sale), y en lista. */
const TodayCard = ({entries, workedToday, now, startTime, endTime}: TodayCardProps) => {
	const nowMinute = now.hour() * 60 + now.minute();
	const [from, to] = useMemo(() => {
		const starts = entries.map(e => minuteOfDay(e.startAt));
		const ends = entries.map(e => e.endAt ? minuteOfDay(e.endAt) : nowMinute);
		return [Math.min(6 * 60, ...starts), Math.max(22 * 60, ...ends)];
	}, [entries, nowMinute]);
	const pos = (m: number) => `${(m - from) * 100 / (to - from)}%`;
	const ref = (t?: string) => t ? parseInt(t.substring(0, 2)) * 60 + parseInt(t.substring(3, 5)) : undefined;
	const refStart = ref(startTime);
	const refEnd = ref(endTime);
	const ticks = [];
	for (let h = Math.ceil(from / 60); h <= Math.floor(to / 60); h += 4) {
		ticks.push(h);
	}

	return (
		<Card elevation={0} sx={panelSx}>
			<SectionTitle action={entries.length > 0 &&
				<Typography variant="body2" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{duration(workedToday)}</Typography>}>
				Hoy
			</SectionTitle>

			<Box sx={{position: "relative", height: 28, mb: 3}}>
				<Box sx={{position: "absolute", inset: "8px 0", borderRadius: 2, bgcolor: "grey.100"}}/>
				{refStart !== undefined && refEnd !== undefined && (
					<Box sx={{
						position: "absolute", top: 8, bottom: 8, left: pos(refStart), width: `calc(${pos(refEnd)} - ${pos(refStart)})`,
						bgcolor: "grey.200", borderRadius: 2,
					}}/>
				)}
				{entries.map(e => {
					const start = minuteOfDay(e.startAt);
					const end = e.endAt ? minuteOfDay(e.endAt) : nowMinute;
					return <Box key={e.id} sx={{
						position: "absolute", top: 4, bottom: 4, left: pos(start),
						width: `max(4px, calc(${pos(end)} - ${pos(start)}))`, borderRadius: 2,
						bgcolor: e.status === "OPEN" ? COLOR_VERDE_ARTECO : e.status === "INCOMPLETE" ? "warning.main" : "primary.main",
					}}/>;
				})}
				<Box sx={{position: "absolute", top: 0, bottom: 0, left: pos(nowMinute), width: 2, bgcolor: COLOR_AZUL_OSCURO, borderRadius: 1}}/>
				{ticks.map((h, i) => (
					<Typography key={h} variant="caption" color="text.disabled" sx={{
						position: "absolute", top: 30, left: pos(h * 60), fontSize: 11,
						// Las de los extremos, hacia dentro, para que no se salgan de la tarjeta.
						transform: i === 0 && h * 60 <= from ? "none" : i === ticks.length - 1 && h * 60 >= to ? "translateX(-100%)" : "translateX(-50%)",
					}}>{h}:00</Typography>
				))}
			</Box>

			{entries.length === 0 ? (
				<Typography variant="body2" color="text.secondary">Aún no hay tramos hoy.</Typography>
			) : (
				<Stack spacing={1}>
					{entries.map(e => (
						<Stack key={e.id} direction="row" alignItems="center" spacing={1.5}
							   sx={{py: 1, px: 1.5, borderRadius: 2, bgcolor: "grey.50"}}>
							<Typography variant="body2" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums", flexGrow: 1}}>
								{hhmm(e.startAt)} → {e.endAt ? hhmm(e.endAt) : "ahora"}
							</Typography>
							{e.status === "COMPLETE"
								? <Typography variant="body2" color="text.secondary">{duration(e.workedMinutes)}</Typography>
								: <Chip size="small" label={e.status === "OPEN" ? "En curso" : STATUS_LABEL[e.status || ""]}
										color={STATUS_COLOR[e.status || ""]} variant="outlined"/>}
						</Stack>
					))}
				</Stack>
			)}
		</Card>
	);
};

/** Lo fichado del mes frente a lo previsto, y los días que piden atención. */
const MonthCard = ({month, onPropose}: { month: TimeClockPersonMonth, onPropose: () => void }) => {
	const worked = month.workedHours || 0;
	const expected = month.expectedHours || 0;
	const monthName = dayjs().format("MMMM");
	const Stat = ({label, value, warn}: { label: string, value: number, warn: boolean }) => (
		<Box sx={{
			flex: 1, p: 1.5, borderRadius: 2,
			bgcolor: warn ? (t => alpha(t.palette.warning.main, 0.1)) : "grey.50",
		}}>
			<Typography variant="h6" fontWeight={700} color={warn ? "warning.dark" : "text.primary"}>{value}</Typography>
			<Typography variant="caption" color="text.secondary">{label}</Typography>
		</Box>
	);

	return (
		<Card elevation={0} sx={panelSx}>
			<SectionTitle>Este mes · {monthName.charAt(0).toUpperCase() + monthName.substring(1)}</SectionTitle>
			<Stack direction="row" alignItems="baseline" spacing={1}>
				<Typography variant="h4" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{hours(worked)}</Typography>
				<Typography variant="body2" color="text.secondary">de {hours(expected)} previstas</Typography>
			</Stack>
			<LinearProgress variant="determinate" value={expected > 0 ? Math.min(100, worked * 100 / expected) : 0}
							sx={{my: 2, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": {borderRadius: 3}}}/>
			<Stack direction="row" spacing={1.5}>
				<Stat label="Días incompletos" value={month.incompleteDays || 0} warn={(month.incompleteDays || 0) > 0}/>
				<Stat label="Días sin fichar" value={month.missingDays || 0} warn={(month.missingDays || 0) > 0}/>
			</Stack>
			{(month.missingDays || 0) > 0 && (
				<Button size="small" startIcon={<AddRoundedIcon/>} onClick={onPropose}
						sx={{mt: 1.5, textTransform: "none", fontWeight: 600}}>
					Proponer un día que no fiché
				</Button>
			)}
		</Card>
	);
};

interface CorrectionsCardProps {
	incomplete: TimeClockEntryDto[];
	corrections: TimeClockCorrectionDto[];
	onCorrect: (e: TimeClockEntryDto) => void;
	onPropose: () => void;
}

/** Los días incompletos con su «Corregir» y las correcciones ya enviadas con su estado. */
const CorrectionsCard = ({incomplete, corrections, onCorrect, onPropose}: CorrectionsCardProps) => (
	<Card elevation={0} sx={{...panelSx, height: "100%"}}>
		<SectionTitle action={
			<Button size="small" variant="outlined" startIcon={<AddRoundedIcon/>} onClick={onPropose}
					sx={{textTransform: "none", fontWeight: 600, borderRadius: 2, whiteSpace: "nowrap"}}>
				Proponer un día
			</Button>
		}>
			Días por corregir
		</SectionTitle>

		{incomplete.length === 0 ? (
			<Stack direction="row" spacing={1} alignItems="center" sx={{color: "success.main"}}>
				<CheckCircleRoundedIcon fontSize="small"/>
				<Typography variant="body2" color="text.secondary">Todo en orden: ningún día incompleto.</Typography>
			</Stack>
		) : (
			<Stack spacing={1}>
				{incomplete.map(e => (
					<Stack key={e.id} direction="row" alignItems="center" spacing={1.5} sx={{
						p: 1.5, borderRadius: 2, bgcolor: t => alpha(t.palette.warning.main, 0.08),
					}}>
						<WarningAmberRoundedIcon color="warning"/>
						<Box sx={{flexGrow: 1, minWidth: 0}}>
							<Typography variant="body2" fontWeight={600}>{longDay(e.workDate)}</Typography>
							<Typography variant="caption" color="text.secondary">Entrada {hhmm(e.startAt)} · sin salida</Typography>
						</Box>
						{e.pendingCorrection
							? <Chip size="small" color="warning" variant="outlined" label="En revisión"/>
							: <Button size="small" variant="contained" disableElevation onClick={() => onCorrect(e)}
									  sx={{textTransform: "none", fontWeight: 600, borderRadius: 2}}>Corregir</Button>}
					</Stack>
				))}
			</Stack>
		)}

		{corrections.length > 0 && (
			<>
				<Typography variant="overline" color="text.secondary" display="block" sx={{mt: 3, mb: 1}}>
					Tus correcciones
				</Typography>
				<Stack spacing={1}>
					{corrections.map(c => (
						<Stack key={c.id} direction="row" alignItems="center" spacing={1.5}
							   sx={{py: 1, px: 1.5, borderRadius: 2, bgcolor: "grey.50"}}>
							<Box sx={{flexGrow: 1, minWidth: 0}}>
								<Typography variant="body2" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums"}}>
									{shortDay(c.workDate)} · {hhmm(c.newStartAt ?? c.previousStartAt)} – {hhmm(c.newEndAt ?? c.previousEndAt)}
								</Typography>
								<Typography variant="caption" color="text.secondary" sx={{display: "block", overflowWrap: "anywhere"}}>
									{c.reviewComment ? `${c.reason} — RR. HH.: ${c.reviewComment}` : c.reason}
								</Typography>
							</Box>
							<Chip size="small" label={STATUS_LABEL[c.status || ""]} color={STATUS_COLOR[c.status || ""]}/>
						</Stack>
					))}
				</Stack>
			</>
		)}
	</Card>
);

interface CorrectionDialogProps {
	entry?: TimeClockEntryDto;
	onClose: () => void;
	onSaved: () => void;
}

const CorrectionDialog = ({entry, onClose, onSaved}: CorrectionDialogProps) => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(EmpTimeClockRestControllerApiFactory);
	const [workDate, setWorkDate] = useState<string>(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
	const [start, setStart] = useState<string>(entry ? "" : "09:00");
	const [end, setEnd] = useState<string>("");
	const [reason, setReason] = useState<string>("");
	const [saving, setSaving] = useState(false);

	const save = () => {
		const req: TimeClockCorrectionRequest = entry
			? {entryId: entry.id, newStart: start || undefined, newEnd: end || undefined, reason}
			: {workDate, newStart: start, newEnd: end, reason};
		setSaving(true);
		apiManager.execute(ctrl.proposeCorrection(req), "Corrección enviada a RR. HH.")
			.then(onSaved)
			.catch(() => undefined)
			.finally(() => setSaving(false));
	};

	const valid = reason.trim().length > 0 && (entry ? (start || end) : (workDate && start && end));

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="xs" PaperProps={{sx: {borderRadius: 3}}}>
			<DialogTitle sx={{fontWeight: 700, pb: 0.5}}>
				{entry ? `Corregir el ${shortDay(entry.workDate)}` : "Proponer un día sin fichar"}
			</DialogTitle>
			<Typography variant="body2" color="text.secondary" sx={{px: 3}}>
				RR. HH. revisará la propuesta; hasta entonces el registro no cambia.
			</Typography>
			<DialogContent>
				<Grid container spacing={2} sx={{mt: 0.5}}>
					{entry && (
						<Grid item xs={12}>
							<Typography variant="body2" color="text.secondary">
								Consta la entrada a las {hhmm(entry.startAt)} y {entry.endAt ? `la salida a las ${hhmm(entry.endAt)}` : "ninguna salida"}.
								Indica la hora real que falta o que cambia.
							</Typography>
						</Grid>
					)}
					{!entry && (
						<Grid item xs={12}>
							<TextField fullWidth type="date" label="Día" value={workDate}
									   InputLabelProps={{shrink: true}}
									   inputProps={{max: dayjs().format("YYYY-MM-DD")}}
									   onChange={e => setWorkDate(e.target.value)}/>
						</Grid>
					)}
					<Grid item xs={6}>
						<TextField fullWidth type="time" label="Entrada" value={start}
								   InputLabelProps={{shrink: true}}
								   helperText={entry ? "Solo si cambia" : undefined}
								   onChange={e => setStart(e.target.value)}/>
					</Grid>
					<Grid item xs={6}>
						<TextField fullWidth type="time" label="Salida" value={end}
								   InputLabelProps={{shrink: true}}
								   onChange={e => setEnd(e.target.value)}/>
					</Grid>
					<Grid item xs={12}>
						<TextField fullWidth multiline minRows={2} label="Motivo" value={reason} required
								   onChange={e => setReason(e.target.value)}/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 2.5}}>
				<Button onClick={onClose} sx={{textTransform: "none"}}>Cancelar</Button>
				<Button variant="contained" disableElevation disabled={!valid || saving} onClick={save}
						sx={{textTransform: "none", fontWeight: 600, borderRadius: 2}}>Enviar a RR. HH.</Button>
			</DialogActions>
		</Dialog>
	);
};
